import { UseQueryResult, useQuery, UseQueryOptions } from '@tanstack/react-query';
import { ResponseDtoSetPermission } from 'src/generated/services/TFinancialApi';
import { tFinancialService } from 'src/infra/services/tFinancialService';
import { cacheKeysUtil } from 'src/utils/cacheKeysUtil';
import { PermissionType } from 'src/types/permission';
// Import from the new userDetails module
import { useUserDetailsQuery } from './userDetails';

// Re-export for backward compatibility
export { useUserDetailsQuery as useUserDetails };

// Original hook for backward compatibility
export const useUserPermissions = (): UseQueryResult<ResponseDtoSetPermission> =>
  useQuery(cacheKeysUtil.userPermission(), async (): Promise<ResponseDtoSetPermission> => {
    const response = await tFinancialService.api.getLoggedInUserPermissions();
    const { data } = response;
    return data;
  });

// New hook for permission types with query options
export const useUserPermissionTypes = (options?: UseQueryOptions<PermissionType[]>): UseQueryResult<PermissionType[]> =>
  useQuery<PermissionType[]>(
    cacheKeysUtil.userPermissionTypes(),
    async (): Promise<PermissionType[]> => {
      const response = await tFinancialService.api.getLoggedInUserPermissionTypes();
      // Convert the string array to PermissionType array with type assertion
      return (response.data?.data || []) as PermissionType[];
    },
    {
      // Default options for better performance - use cached data, avoid duplicate calls
      staleTime: 5 * 60 * 1000, // 5 minutes - data is fresh, no refetch needed
      cacheTime: 10 * 60 * 1000, // 10 minutes - keep in cache
      refetchOnWindowFocus: false, // Don't refetch when window regains focus
      refetchOnMount: false, // Don't refetch on every mount - use cache
      ...options, // Allow overriding with custom options
    }
  );

// Hook to check if user has a specific permission
export const useHasPermission = (
  requiredPermission: PermissionType
): {
  hasPermission: boolean;
  isLoading: boolean;
} => {
  // Use cached permissions data - no need to refetch on every mount
  // Permissions are prefetched in UserDataSynchronizer and cached
  const { data: permissions, isLoading } = useUserPermissionTypes();

  // While loading, we don't know yet if the user has permission
  // Return isLoading: true so the component can show a loading state
  if (isLoading) {
    return { hasPermission: false, isLoading: true };
  }

  // If we have permissions data, check if the user has the required permission
  return {
    hasPermission: permissions?.includes(requiredPermission) || false,
    isLoading: false,
  };
};
